import {
  Button,
  ButtonVariantEnum,
} from "@booking/components/atoms/forms/Button";
import { type ImageProps } from "@booking/components/atoms/media/Image";
import { ImageProviderEnum, ImageVariantEnum, SizeEnum } from "@booking/enums";
import { gaClickAttrs } from "@booking/utils/tracking";
import {
  faArrowPointer,
  faGolfBallTee,
  faMedal,
} from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { DEFAULT_LABEL_ICON, LABEL_ICONS } from "@search/api/constants";
import { type SearchResult } from "@search/api/types";
import { PackageDetails } from "@search/components/compounds/PackageDetails";
import { Label } from "@search/components/molecules/Label";
import { Location } from "@search/components/molecules/Location";
import { Price } from "@search/components/molecules/Price";
import { Rating } from "@search/components/molecules/Rating";
import { customPluralize } from "@search/utils/helpers";
import { useWindowSize } from "@uidotdev/usehooks";
import clsx from "clsx";
import pluralize from "pluralize";

import { Figure } from "@/components/molecules/media/Figure";
import { ColorEnum } from "@/enums";
import { type CommonProps } from "@/types";
import { containerCSSVars, setCSSVariables } from "@/utils/helpers";

import styles from "./ResultCard.module.css";

const ref = "result-card";

interface ResultCardProps extends Partial<CommonProps> {
  details: SearchResult;
  dateParam: string;
  nightsParam?: number;
}

function generateSlug(
  slug: string,
  date: string,
  nights?: number,
  anchor: string = "",
  packageId?: number,
) {
  if (!date && !packageId && !nights) {
    return `${slug}${anchor}`;
  }

  const params: { arrival_date?: string; package?: string; nights?: string } =
    {};
  if (date) {
    params.arrival_date = date;
  }
  if (packageId) {
    params.package = packageId.toString();
  }
  if (nights && nights > 0) {
    params.nights = nights.toString();
  }

  const search = new URLSearchParams(params).toString();
  return `${slug}?${search}${anchor}`;
}

function overlay(details: SearchResult) {
  const { bookOnline, fourBall } = details;

  if (fourBall) {
    return (
      <aside className={styles["four-ball"]}>
        <FontAwesomeIcon icon={faGolfBallTee} />
        <span>4-ball offer</span>
      </aside>
    );
  }

  if (bookOnline) {
    return (
      <aside>
        <FontAwesomeIcon icon={faArrowPointer} />
        <span>Online Bookable</span>
      </aside>
    );
  }

  return null;
}

function useResultCard(props: ResultCardProps) {
  const { details, dateParam, nightsParam } = props;
  const { labels = [], transportation, venue, featured } = details;
  const {
    breadcrumbs = [],
    distance,
    image,
    rating = 0,
    ratingCount = 0,
    totalPackageCount = 0,
  } = venue;
  const className = clsx(
    styles[ref],
    featured && styles["feature-card"],
    props.className,
  );

  if (image) {
    image.provider = ImageProviderEnum.CLOUDINARY;
    image.variant = ImageVariantEnum.SEARCH;
  }

  const vars = containerCSSVars(useWindowSize(), image);
  let transport = "";
  if (transportation.flights) {
    transport = "Flights, ";
  } else if (transportation.eurotunnel) {
    transport = "Le Shuttle, ";
  }

  return {
    className,
    image: image as ImageProps,
    labels: labels.map((label) => {
      const icon =
        LABEL_ICONS[label.slug as keyof typeof LABEL_ICONS] ||
        DEFAULT_LABEL_ICON;

      return { text: label.name, icon };
    }),
    transport,
    totalPackageCount,
    rating,
    ratingCount,
    vars: setCSSVariables(vars),
    venue: {
      breadcrumbs,
      distance,
      countryCode: venue.countryCode,
      name: venue.name,
      packageSlug: generateSlug(
        venue.slug,
        dateParam,
        nightsParam,
        "#offers",
        details.packageId,
      ),
      rating,
      ratingCount,
      totalPackageCount,
      venueSlug: generateSlug(
        venue.slug,
        dateParam,
        nightsParam,
        "",
        details.packageId,
      ),
    },
  };
}

function ResultCard(props: ResultCardProps) {
  const { className, image, labels, vars, venue, transport } =
    useResultCard(props);
  const { details } = props;
  const { nights, price, rounds, featured } = details;

  const {
    breadcrumbs,
    distance = 0,
    countryCode,
    name,
    packageSlug,
    rating,
    ratingCount,
    totalPackageCount,
    venueSlug,
  } = venue;

  return (
    <article className={className} style={vars}>
      {featured && (
        <aside className={styles["feature-aside"]}>
          <FontAwesomeIcon icon={faMedal} />
          <span>Featured</span>
        </aside>
      )}
      {overlay(details)}

      <header>
        <Button
          color={ColorEnum.DARKBLUE}
          variant={ButtonVariantEnum.PLAIN}
          href={venueSlug}
          target="_blank"
          {...gaClickAttrs({
            eventGroup: "Result card",
            click: "find_out_more_click",
          })}
        >
          {name}
        </Button>

        <Location countryCode={countryCode} location={breadcrumbs} />

        {distance > 0 && (
          <span>{`${pluralize("mile", distance, true)} away`}</span>
        )}

        <Rating count={ratingCount} rating={rating} />
      </header>

      <section>
        <p>
          {transport}
          {`${customPluralize("night", nights, true)}, ${customPluralize("round", rounds, true)}`}
        </p>

        <PackageDetails {...props.details} />
      </section>

      <Figure
        image={{ ...image }}
        href={venueSlug}
        target="_blank"
        {...gaClickAttrs({ eventGroup: "Result card", click: "image_click" })}
      >
        {labels.length > 0 &&
          labels.map(({ icon, text }, index) => (
            <Label
              color={ColorEnum.DARKGREEN}
              key={`label_${index}`}
              icon={icon}
              text={text}
            />
          ))}
      </Figure>

      <footer>
        <Price amount={price} baseline />
        <Button
          color={ColorEnum.DARKGREEN}
          href={packageSlug}
          size={SizeEnum.LARGE}
          variant={ButtonVariantEnum.SOLID}
          target="_blank"
          {...gaClickAttrs({
            eventGroup: "Result card",
            click: "find_out_more_click",
          })}
        >
          See all {pluralize("holiday", totalPackageCount, true)}
        </Button>
      </footer>
    </article>
  );
}

export default ResultCard;
export { ResultCard, type ResultCardProps };
